import React, { useCallback, useEffect, useRef } from 'react';

function IconXClose({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M17.2929 5.29302C17.6834 4.90249 18.3164 4.90249 18.707 5.29302C19.0975 5.68354 19.0975 6.31655 18.707 6.70708L13.414 12L18.707 17.293L18.7753 17.3692C19.0957 17.762 19.0731 18.341 18.707 18.7071C18.3408 19.0732 17.7618 19.0958 17.3691 18.7754L17.2929 18.7071L11.9999 13.4141L6.70696 18.7071C6.31643 19.0976 5.68342 19.0976 5.29289 18.7071C4.90237 18.3166 4.90237 17.6835 5.29289 17.293L10.5859 12L5.29289 6.70708L5.22453 6.63091C4.90418 6.23813 4.92678 5.65913 5.29289 5.29302C5.65901 4.9269 6.23801 4.90431 6.63078 5.22466L6.70696 5.29302L11.9999 10.586L17.2929 5.29302Z"
        fill="currentColor"
      />
    </svg>
  );
}

export interface CompletionModalProps {
  open: boolean;
  onDismiss?: () => void;
  onKeepLearning?: () => void;
  hasNext: boolean;
  nextLabel?: string | null;
  /** `true` → task headline + “Start Next Task”; `false` → module headline + “Continue”. */
  isAction?: boolean;
}

/**
 * Post-completion modal after the celebration dismisses (Learning Center task flow).
 */
const CompletionModal: React.FC<CompletionModalProps> = ({
  open,
  onDismiss,
  onKeepLearning,
  hasNext,
  nextLabel,
  isAction = true,
}) => {
  const headline = isAction ? "You've completed this task! 🎉" : "You've completed this module! 🎉";
  const closeBtnRef = useRef<HTMLButtonElement>(null);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss?.();
    },
    [onDismiss]
  );

  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const focusTimer = window.setTimeout(() => closeBtnRef.current?.focus(), 0);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    };
  }, [open, handleKeyDown]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="completion-modal-title"
      onClick={onDismiss}
      className="completion-modal"
    >
      <div className="completion-modal__card" onClick={(e) => e.stopPropagation()}>
        <button
          ref={closeBtnRef}
          type="button"
          onClick={onDismiss}
          aria-label="Close"
          className="completion-modal__close"
        >
          <IconXClose className="completion-modal__close-icon" />
        </button>

        <h2 id="completion-modal-title" className="completion-modal__title">
          {headline}
        </h2>

        {hasNext ? (
          <div className="completion-modal__next-panel">
            {nextLabel ? (
              <div className="completion-modal__next-copy">
                <span className="completion-modal__next-eyebrow">Next</span>
                <span className="completion-modal__next-title">{nextLabel}</span>
              </div>
            ) : null}
            <button
              type="button"
              className="btn btn-lg btn-fill btn-primary completion-modal__cta"
              onClick={onKeepLearning}
            >
              {isAction ? 'Start Next Task' : 'Continue'}
            </button>
          </div>
        ) : (
          <div className="completion-modal__footer-only">
            <button
              type="button"
              className="btn btn-lg btn-fill btn-primary completion-modal__cta"
              onClick={onKeepLearning}
            >
              Return Home
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompletionModal;
